import { Application, Controller } from "@hotwired/stimulus"
import { Age } from "@dogheroes/age"
import { CaloriesCalculator } from "@dogheroes/calories-calculator"
import { Dog } from "@dogheroes/dog"
import { SavedCart } from "@dogheroes/saved-cart"
import { render, tag } from "@dogheroes/utils"

class DogHeroesApplication extends Application {
  constructor() {
    super()
    this.debug = location.hostname !== "dogheroes.it"
    this.Controller = Controller
    this.autoloadControllers()
  }

  autoloadControllers() {
    Object.keys(
      JSON.parse(document.querySelector("script[type=importmap]").text).imports
    ).forEach((modulePath) => {
      if (modulePath.endsWith("-controller")) {
        import(modulePath).then((module) => {
          let controllerName = modulePath
            .split("/")
            .pop()
            .replace("-controller", "")
            .replace(".js", "")
          try {
            this.register(controllerName, module.default)
          } catch (e) {
            console.error(
              `Error registering Stimulus controller '${controllerName}' from module '${modulePath}':`,
              e
            )
          }
        })
      }
    })
  }
}

// --- GTM data layer -------------------------------------------------------
// Single choke-point for every storefront GTM event (post-quiz recipe flow,
// PDP add-to-subscription, account portal). Keeping the push here means the
// payload shape — and `user_data` in particular — stays consistent with the
// quiz events, regardless of who builds the rest of the payload.
//
// The account portal builds its payload server-side (state is only known
// after the mutation) and renders it as an inert
// <script type="application/json" data-gtm-event> blob; we pick it up on load
// so the actual push still happens client-side, in one place.

function shopifyClientId() {
  const match = document.cookie.match(/(?:^|; )shopify_client_id=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function buildUserData() {
  const customer = window.DogHeroes.customer || {}
  const lead = (key) => (localStorage.getItem(key) || "").trim() || null

  let leadFirstName = null
  let leadLastName = null
  const leadName = lead("dogheroes.customer_name")
  if (leadName) {
    const [first, ...rest] = leadName.split(/\s+/)
    leadFirstName = first || null
    leadLastName = rest.length > 0 ? rest.join(" ") : null
  }

  return {
    first_name: customer.first_name || leadFirstName,
    last_name: customer.last_name || leadLastName,
    email: customer.email || lead("dogheroes.customer_email"),
    customer_id: customer.id || null,
    shopify_client_id: shopifyClientId(),
  }
}

function pushToDataLayer(payload) {
  window.dataLayer = window.dataLayer || []
  const fullPayload = { ...payload, user_data: buildUserData() }
  if (location.hostname !== "dogheroes.it")
    console.log("Pushing to dataLayer:", fullPayload)
  window.dataLayer.push(fullPayload)
}

// A dog in the GTM data layer, nested under `dogs: [...]` with the SAME
// nomenclature as the quiz events (collectDogsForStep), so GTM's dogs.* variables
// resolve identically across quiz and post-quiz. The flat/Klaviyo reshaping
// happens in GTM, not here — we just push consistent data.
function serializeDog(dog) {
  return {
    index: dog.index,
    name: dog.name,
    breed: dog.breed,
    additional_breed: dog.additional_breed,
    sex: dog.sex,
    sterilization: dog.sterilization,
    birthday_type: dog.birthday_type,
    birthday: dog.birthday,
    weight: dog.weight,
    build: dog.build,
    activity: dog.activity,
    hunger: dog.hunger,
    has_diseases: dog.has_diseases,
    diet: dog.diet,
    allergies: dog.allergies,
    diseases: dog.diseases,
  }
}

// The dog's recipes as text, e.g. "Chicken Bowl | Dispensa, Beef Bowl | Surgelato".
// `recipes` is the per-variant data stored on the saved cart.
function recipesText(recipes) {
  return recipes
    .map((recipe) =>
      [recipe.productTitle, recipeTypeLabel(recipe.recipeType)]
        .filter(Boolean)
        .join(" | ")
    )
    .join(", ")
}

// total_price: the dog's recipes over the box (€). average_price: the same
// spread per day. Recipe prices are stored per-pack in cents.
function dogPriceFields(recipes) {
  let totalCents = recipes.reduce(
    (sum, recipe) => sum + recipe.quantity * recipe.price,
    0
  )
  let total = round2(totalCents / 100)
  return {
    average_price: round2(total / window.DogHeroes.intervalDays),
    total_price: total,
  }
}

// The GA4 `ecommerce` block (Stape-compatible) for a dog's chosen recipes.
// `subscription` is the per-variant map saved on the cart (keyed by variant id);
// the product/variant details are looked up in DogHeroes.variants by variant id.
// value is the box total of the recipes, so it always matches their items.
function recipeEcommerce(subscription) {
  let items = recipeItems(subscription)
  let value = round2(
    items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  )
  return { value, currency: "EUR", items }
}

function recipeItems(subscription) {
  let variants = window.DogHeroes.variants || []
  return Object.entries(subscription).map(([variantId, recipe]) => {
    let variant =
      variants.find((v) => String(v.variant_id) === String(variantId)) || {}
    return {
      item_id: variant.product_id != null ? String(variant.product_id) : "",
      item_sku: variant.sku || "",
      item_variant: String(variantId),
      item_name: recipe.productTitle || variant.product_title || "",
      variant_name: variant.variant_title || "",
      item_category: variant.product_type || "",
      item_brand: "DogHeroes",
      item_url: variant.product_handle
        ? `/products/${variant.product_handle}?variant=${variantId}`
        : "",
      price: round2(recipe.price / 100),
      imageURL: variant.image || null,
      quantity: recipe.quantity,
    }
  })
}

function recipeTypeLabel(recipeType) {
  if (recipeType === "frozen") return "Surgelato"
  if (recipeType === "pantry") return "Dispensa"
  return null
}

function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

// Push any server-rendered event blob once it's in the DOM, marking it
// consumed so we never push the same one twice (initial load + Turbo visit).
function consumePendingDataLayerEvent() {
  document
    .querySelectorAll(
      "script[type='application/json'][data-gtm-event]:not([data-gtm-consumed])"
    )
    .forEach((node) => {
      node.setAttribute("data-gtm-consumed", "")
      try {
        pushToDataLayer(JSON.parse(node.textContent))
      } catch (error) {
        console.error("Invalid [data-gtm-event] payload:", error)
      }
    })
}

window.DogHeroes = Object.assign(window.DogHeroes || {}, {
  render,
  tag,
  Age,
  CaloriesCalculator,
  Dog,
  SavedCart,
  pushToDataLayer,
  buildUserData,
  serializeDog,
  recipesText,
  dogPriceFields,
  recipeEcommerce,
})
window.DogHeroes.algorithmData = JSON.parse(
  document.querySelector('meta[name="dogheroes:algorithm-data"]').content
)

consumePendingDataLayerEvent()
document.addEventListener("turbo:load", consumePendingDataLayerEvent)
document.addEventListener("turbo:render", consumePendingDataLayerEvent)

window.Stimulus = DogHeroesApplication.start()
